import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { CSSProperties, ReactNode, useRef } from 'react';
import { Bubble } from 'yoso';
import './Progress.scss';

export type ProgressProps = Omit<JSX.IntrinsicElements['div'], 'onChange'> & {
  bubble?: ReactNode;
  max?: number;
  min?: number;
  value?: number;
  offset?: number | string;
  readonly?: boolean;
  onChange?: (value: number) => void;
};
export const Progress = ({
  max = 1,
  min = 0,
  value,
  offset,
  bubble = <Bubble className="progress-thumb-bubble">{value}</Bubble>,
  children,
  className,
  style,
  readonly = true,
  onChange,
  ...props
}: ProgressProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const bind = useDrag(
    (state) => {
      if (readonly || !trackRef.current) return;

      const { x: trackX, width: trackWidth } = trackRef.current!.getBoundingClientRect();
      const [x] = state.xy;

      // get normalized position x
      const r = (x - trackX) / trackWidth;

      // clamp r to 0 ~ 1
      const v = Math.min(1, Math.max(0, r));

      // normal to value
      const value = min + v * (max - min);

      onChange?.(value);
    },
    {
      axis: 'x',
    }
  );

  const v = ((value || 0) - min) / (max - min);

  return (
    <div
      className={classNames('progress', className)}
      style={
        {
          ...style,
          '--progress-value': value !== undefined ? v : undefined,
          '--progress-offset': typeof offset === 'number' ? `${offset}px` : offset,
        } as CSSProperties
      }
      {...props}>
      <div className="progress-track" ref={trackRef}>
        <div className="progress-bar">
          <div className="progress-thumb" {...bind()}>
            {bubble}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
